import { Program } from '../types/content'

export const programs: Program[] = [
  {
    slug: 'hormone-optimization-men',
    name: 'Hormone Optimization for Men',
    category: 'Hormone',
    summary:
      'Testosterone therapy directed by a licensed clinician, with lab work, titration and ongoing review.',
    description:
      'A complete testosterone replacement program for men with clinically confirmed low testosterone. Your clinician reviews your labs and symptoms, sets a starting dose, and adjusts it as your follow-up panels come back.',
    priceFrom: 129,
    cadence: 'per month',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ddf7d1bd-f11d-4b7f-85c0-4e46120dd09c.jpg',
    eligibility: 'Men 21+ with lab-confirmed low testosterone',
    includes: [
      'Baseline 62-marker hormone and metabolic panel',
      'Clinician review and written protocol',
      'Medication shipped from a US-licensed pharmacy',
      'Follow-up labs at week 8 and month 6',
      'Unlimited messaging with your care team',
    ],
    protocol: [
      {
        title: 'Baseline panel',
        detail:
          'Total and free testosterone, estradiol, SHBG, LH, FSH, PSA, CBC, CMP and lipids — collected at home or at a partner lab.',
      },
      {
        title: 'Clinician visit',
        detail:
          'An asynchronous or live visit with a licensed clinician who confirms eligibility and writes your protocol.',
      },
      {
        title: 'Titration',
        detail:
          'Dose is adjusted against your week-8 panel and symptom review, not a fixed schedule.',
      },
    ],
    medications: [
      { name: 'Testosterone Cypionate', form: 'Injectable, 200mg/mL', note: 'Weekly or twice weekly' },
      { name: 'Anastrozole', form: 'Oral tablet', note: 'Only when estradiol requires it' },
      { name: 'Enclomiphene', form: 'Oral capsule', note: 'Fertility-preserving alternative' },
    ],
  },
  {
    slug: 'hormone-therapy-women',
    name: 'Hormone Therapy for Women',
    category: 'Hormone',
    summary:
      'Perimenopause and menopause care with estradiol, progesterone and low-dose testosterone where indicated.',
    description:
      'Symptom-led care for women in perimenopause and menopause. Your clinician builds a protocol around what you are actually experiencing — sleep, temperature, mood, libido, cycle changes — and confirms it with lab work.',
    priceFrom: 99,
    cadence: 'per month',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/26deb3e1-2c41-40d4-93bf-5fd27d7f7b3e.jpg',
    eligibility: 'Women 30+ with perimenopausal or menopausal symptoms',
    includes: [
      'Baseline hormone, thyroid and metabolic panel',
      'Symptom assessment reviewed by a clinician',
      'Transdermal or oral medication options',
      'Quarterly protocol review',
      'Unlimited messaging with your care team',
    ],
    protocol: [
      {
        title: 'Symptom assessment',
        detail:
          'A structured intake covering vasomotor symptoms, sleep, mood, cycle history and cardiovascular risk.',
      },
      {
        title: 'Baseline panel',
        detail: 'Estradiol, FSH, progesterone, testosterone, TSH, free T3/T4, CBC, CMP and lipids.',
      },
      {
        title: 'Protocol and review',
        detail: 'Starting protocol shipped within 48 hours of approval, reviewed every 90 days.',
      },
    ],
    medications: [
      { name: 'Estradiol', form: 'Transdermal cream or patch', note: 'Daily' },
      { name: 'Progesterone', form: 'Oral capsule', note: 'Nightly, cyclic or continuous' },
      { name: 'Testosterone', form: 'Transdermal cream', note: 'Low dose, where indicated' },
    ],
  },
  {
    slug: 'weight-management',
    name: 'Weight Management',
    category: 'Metabolic',
    summary:
      'GLP-1 therapy with metabolic labs, dose escalation and the nutrition support that makes it stick.',
    description:
      'A medically supervised weight program built on GLP-1 and GIP/GLP-1 therapy. Republic prices the medication transparently and pairs it with the labs and coaching that protect lean mass and long-term results.',
    priceFrom: 179,
    cadence: 'per month',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/788cc45f-10d6-4e88-b016-ba6af3b006f3.jpg',
    eligibility: 'Adults with BMI 27+ or BMI 25+ with a metabolic condition',
    includes: [
      'Baseline metabolic panel with A1c, insulin and lipids',
      'Clinician-directed escalation schedule',
      'Medication and supplies shipped monthly',
      'Nutrition and resistance-training guidance',
      'Side-effect support within one business day',
    ],
    protocol: [
      {
        title: 'Metabolic baseline',
        detail: 'A1c, fasting insulin, glucose, lipids, kidney and liver function, thyroid.',
      },
      {
        title: 'Escalation',
        detail:
          'Dose increases are earned, not automatic — tolerability and results are reviewed at every step.',
      },
      {
        title: 'Maintenance',
        detail: 'A written plan for holding results, including the lowest effective dose.',
      },
    ],
    medications: [
      { name: 'Semaglutide', form: 'Injectable', note: 'Weekly' },
      { name: 'Tirzepatide', form: 'Injectable', note: 'Weekly' },
      { name: 'Metformin', form: 'Oral tablet', note: 'Adjunct where appropriate' },
    ],
  },
  {
    slug: 'longevity-peptides',
    name: 'Longevity & Peptides',
    category: 'Longevity',
    summary:
      'Peptide protocols for recovery, body composition and sleep quality, compounded in US pharmacies.',
    description:
      'For patients who want to work on healthspan, not just symptoms. Every peptide Republic dispenses is compounded in a US-licensed 503A or 503B pharmacy and dispensed against a clinician-written protocol.',
    priceFrom: 149,
    cadence: 'per month',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8450246b-3b81-4ced-976d-70721297b7c8.jpg',
    eligibility: 'Adults 21+ after clinician review',
    includes: [
      'Extended longevity panel with inflammatory markers',
      'Clinician-selected peptide protocol',
      'US-compounded medication with lot documentation',
      'Cycling and washout schedule in writing',
      'Progress review every 12 weeks',
    ],
    protocol: [
      {
        title: 'Extended panel',
        detail: 'IGF-1, hs-CRP, ApoB, HbA1c, homocysteine, vitamin D, full hormone profile.',
      },
      {
        title: 'Protocol design',
        detail: 'Peptides selected against a stated goal — recovery, composition, sleep or immunity.',
      },
      {
        title: 'Cycle and reassess',
        detail: 'Defined on- and off-cycles with a follow-up panel before any renewal.',
      },
    ],
    medications: [
      { name: 'Tesamorelin', form: 'Injectable, 10mg', note: 'Nightly, cycled' },
      { name: 'BPC-157', form: 'Injectable or oral', note: 'Recovery protocols' },
      { name: 'NAD+', form: 'Injectable', note: 'Weekly' },
    ],
  },
  {
    slug: 'sexual-health',
    name: 'Sexual Health',
    category: 'Everyday',
    summary: 'Discreet treatment for ED and libido, reviewed by a clinician and shipped unbranded.',
    description:
      'Evidence-based treatment for erectile dysfunction and low libido, with an honest look at the hormonal and cardiovascular causes underneath rather than a default prescription.',
    priceFrom: 39,
    cadence: 'per month',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ddf7d1bd-f11d-4b7f-85c0-4e46120dd09c.jpg',
    eligibility: 'Adults 21+ after clinician review',
    includes: [
      'Focused intake and cardiovascular screening',
      'Clinician review within 24 hours',
      'Generic and combination options',
      'Discreet, unbranded delivery',
      'Dose changes at no extra cost',
    ],
    protocol: [
      { title: 'Intake', detail: 'Medical history, medications, cardiovascular risk and hormone symptoms.' },
      { title: 'Review', detail: 'A licensed clinician confirms safety and selects the right agent and dose.' },
      { title: 'Adjust', detail: 'Message your care team any time to change dose or formulation.' },
    ],
    medications: [
      { name: 'Sildenafil', form: 'Oral tablet or troche', note: 'As needed' },
      { name: 'Tadalafil', form: 'Oral tablet', note: 'Daily or as needed' },
      { name: 'Oxytocin', form: 'Troche', note: 'Libido protocols' },
    ],
  },
  {
    slug: 'hair-and-skin',
    name: 'Hair & Skin',
    category: 'Everyday',
    summary: 'Hair loss and skin protocols using topical and oral compounds at transparent pricing.',
    description:
      'Compounded topicals and orals for androgenic hair loss and skin concerns, with realistic timelines and photo-based progress reviews.',
    priceFrom: 29,
    cadence: 'per month',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/26deb3e1-2c41-40d4-93bf-5fd27d7f7b3e.jpg',
    eligibility: 'Adults 18+ after clinician review',
    includes: [
      'Photo-based assessment',
      'Compounded topical or oral protocol',
      'Three-month supply shipping option',
      'Photo progress review at month 3 and 6',
      'Unlimited messaging with your care team',
    ],
    protocol: [
      { title: 'Assessment', detail: 'Upload photos and history; your clinician grades the pattern and stage.' },
      { title: 'Protocol', detail: 'Topical or oral compound selected for your tolerance and stage.' },
      { title: 'Review', detail: 'Photo comparison at three and six months to confirm response.' },
    ],
    medications: [
      { name: 'Finasteride / Minoxidil', form: 'Compounded topical', note: 'Daily' },
      { name: 'Oral Minoxidil', form: 'Oral tablet', note: 'Low dose, daily' },
      { name: 'Tretinoin', form: 'Compounded cream', note: 'Nightly' },
    ],
  },
]

export function getProgram(slug: string): Program | undefined {
  return programs.find((program) => program.slug === slug)
}
